<template>
    <div class="w-100">
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">{{$t('sentEmails.title')}}</div>
            </div>

            <div class="btn-toolbar justify-content-between mb-0 mt-3" role="toolbar">
                <div class="col-w"></div>
                <div class="input-group">
                    <input v-model="searchQuery"
                           v-on:keyup.enter.stop="massSearch"
                           v-on:keyup.esc.stop="clearFilters"
                           type="text"
                           class="form-control"
                           aria-label="Mass search input"
                           aria-describedby="massSearch" />
                    <button class="btn btn-secondary" @click.stop="massSearch">
                        <i class="fas fa-search"></i>
                        {{ $t('buttons.search.btnText') }}
                    </button>
                    <button class="btn btn-secondary" @click.stop="clearFilters">
                        <i class="fas fa-times"></i>
                        {{ $t('buttons.clearFilters.btnText') }}
                    </button>
                </div>
            </div>

            <div class="card-body">
                <div class="grid-wrapper">
                    <kendo-datasource ref="datasource"
                                      :transport="transportSettings"
                                      :schema-model-fields="schemaModelFields"
                                      :schema-model-id="'id'"
                                      :schema-total="'total'"
                                      :schema-data="'data'"
                                      :page-size="gridOptions.pageable.size"
                                      :server-paging="true"
                                      :server-sorting="true"
                                      :server-filtering="true">
                    </kendo-datasource>

                    <kendo-grid ref="sentEmailsGrid"
                                id="sentEmailsGrid"
                                :data-source-ref="'datasource'"
                                :navigatable="gridOptions.navigatable"
                                :editable="gridOptions.editable"
                                :filterable="gridOptions.filterable"
                                :pageable="gridOptions.pageable"
                                :reorderable="gridOptions.reorderable"
                                :resizable="gridOptions.resizable"
                                :groupable="gridOptions.groupable"
                                :allow-copy="gridOptions.allowCopy"
                                :column-menu="gridOptions.columnMenu"
                                :sortable-mode="gridOptions.sortable.mode"
                                :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                                :sortable-show-indexes="gridOptions.sortable.showIndexes"
                                :selectable="gridOptions.selectable"
                                :scrollable="gridOptions.scrollable"
                                :dataBound="onDatabound">
                        <kendo-grid-column :field="'to'" :template="toCellTemplate" :title="$t('sentEmails.gridHeaders.To')"></kendo-grid-column>
                        <kendo-grid-column :field="'cc'" :template="ccCellTemplate" :title="$t('sentEmails.gridHeaders.Cc')"></kendo-grid-column>
                        <kendo-grid-column :field="'bcc'" :template="bccCellTemplate" :title="$t('sentEmails.gridHeaders.Bcc')"></kendo-grid-column>
                        <kendo-grid-column :field="'subject'" :template="subjectCellTemplate" :title="$t('sentEmails.gridHeaders.Subject')"></kendo-grid-column>
                        <kendo-grid-column :field="'body'" :hidden="true" :template="bodyCellTemplate" :title="$t('sentEmails.gridHeaders.Body')"></kendo-grid-column>
                        <kendo-grid-column :field="'createdBy'" :title="$t('sentEmails.gridHeaders.CreatedBy')"></kendo-grid-column>
                        <kendo-grid-column :field="'hasError'" :title="$t('sentEmails.gridHeaders.HasError')" :template="hasErrorCellTemplate"></kendo-grid-column>
                        <kendo-grid-column :field="'errors'" :hidden="true" :template="errorsCellTemplate" :title="$t('sentEmails.gridHeaders.Errors')"></kendo-grid-column>
                        <kendo-grid-column :field="'createdAtUtc'" :title="$t('sentEmails.gridHeaders.CreatedAtUtc')" :format="customDateTimeFormat"></kendo-grid-column>
                        <kendo-grid-column :field="'sentTimeUtc'" :title="$t('sentEmails.gridHeaders.SentTimeUtc')" :format="customDateTimeFormat"></kendo-grid-column>
                        <kendo-grid-column :field="'errorTimeUtc'" :title="$t('sentEmails.gridHeaders.ErrorTimeUtc')" :format="customDateTimeFormat"></kendo-grid-column>
                        <kendo-grid-column field="btnsColumn" :width="150" :title="$t('actions')" :sortable="false" :filterable="false" :template="btnsTemplate"></kendo-grid-column>

                        <kendo-contextmenu @select="selectContextMenu" :target="'#sentEmailsGrid'" :filter="'td'">
                            <li :id="'details'" class="text-info"><i class="fas fa-eye"></i> {{ $t('buttons.details.btnText') }}</li>
                        </kendo-contextmenu>
                    </kendo-grid>
                </div>
            </div>
        </div>
        <JsonFormatter v-model="sentEmailDetails" :show.sync="showSentEmailDetails"
                       :titleKey="'buttons.details.btnText'"
                       @onClose="onSentEmailDetailsClose">
        </JsonFormatter>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BtnsTemplate from "../../components/grid/StandardBtnsTemplate";
    import JsonFormatter from "../../components/common/json.formatter.modal.vue";
    import { mapGetters } from 'vuex';

    export default {
        components: {
            JsonFormatter
        },
        data() {
            return {
                sentEmailDetails: {},
                showSentEmailDetails: false,
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/notification/listSentEmails',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                searchQuery: '',
                schemaModelFields: {
                    subject: { field: 'subject', type: 'string', nullable: true, editable: false },
                    body: { field: 'body', type: 'string', nullable: true, editable: false },
                    to: { field: 'to', type: 'string', nullable: true, editable: false },
                    cc: { field: 'cc', type: 'string', nullable: true, editable: false },
                    bcc: { field: 'bcc', type: 'string', nullable: true, editable: false },
                    createdBy: { field: 'createdBy', type: 'string', nullable: true, editable: false },
                    errors: { field: 'errors', type: 'string', nullable: true, editable: false },
                    note: { field: 'note', type: 'string', nullable: true, editable: false },
                    hasError: { field: 'hasError', type: 'boolean', nullable: true, editable: false },
                    createdAtUtc: { field: 'createdAtUtc', type: 'date', nullable: true, editable: false },
                    sentTimeUtc: { field: 'sentTimeUtc', type: 'date', nullable: true, editable: false },
                    errorTimeUtc: { field: 'errorTimeUtc', type: 'date', nullable: true, editable: false },
                }
            };
        },
        methods: {
            onDatabound(e) {
                var items = e.sender.items();
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.hasError === true) {
                        $(item).addClass('left-border-danger');
                    } else {
                        $(item).addClass('left-border-success');
                    }
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) };
                } else {
                    return kendo.stringify(options);
                }
            },
            beforeSend: function (req) {
                if (this.authToken) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
                }
            },
            hasErrorCellTemplate(dataItem) {
                let templateHtml =
                    '<span class="badge ' + (dataItem.hasError ? 'badge-danger' : 'badge-success') + '">' + kendo.htmlEncode(dataItem.hasError ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            toCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.to ? dataItem.to.replace('"', '\'') : '') + '">' + (dataItem.to ? dataItem.to : "") + '</span>';

                return templateHtml;
            },
            ccCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.cc ? dataItem.cc.replace('"', '\'') : '') + '">' + (dataItem.cc ? dataItem.cc : "") + '</span>';

                return templateHtml;
            },
            bccCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.bcc ? dataItem.bcc.replace('"', '\'') : '') + '">' + (dataItem.bcc ? dataItem.bcc : "") + '</span>';

                return templateHtml;
            },
            subjectCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.subject ? dataItem.subject.replace('"', '\'') : '') + '">' + (dataItem.subject ? dataItem.subject : "") + '</span>';

                return templateHtml;
            },
            bodyCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.body ? dataItem.body.replace('"', '\'') : '') + '">' + (dataItem.body ? dataItem.body : "") + '</span>';

                return templateHtml;
            },
            errorsCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.errors ? dataItem.errors.replace('"', '\'') : '') + '">' + (dataItem.errors ? dataItem.errors : "") + '</span>';

                return templateHtml;
            },
            refresh() {
                const grid = this.$refs.sentEmailsGrid.kendoWidget();
                if (grid) {
                    grid.dataSource.read();
                }
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.sentEmailsGrid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                let grid = this.$refs.sentEmailsGrid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: [
                            {
                                name: "details",
                                text: "",
                                tooltip: this.$t('buttons.details.btnText'),
                                class: 'btn btn-info',
                                icon: "fas fa-eye",
                                show: true,
                                clickHandler: this.detailsBtnClick
                            },
                        ]
                    }
                };
            },
            detailsBtnClick(args) {
                this.sentEmailDetails = {
                    subject: args.subject,
                    body: args.body,
                    to: args.to,
                    cc: args.cc,
                    bcc: args.bcc,
                    createdBy: args.createdBy,
                    errors: args.errors,
                    note: args.note,
                    createdAtUtc: args.createdAtUtc,
                    sentTimeUtc: args.sentTimeUtc,
                    errorTimeUtc: args.errorTimeUtc,
                    hasError: args.hasError,
                };
                this.showSentEmailDetails = true;
            },
            onSentEmailDetailsClose() {
                this.sentEmailDetails = {};
                this.showSentEmailDetails = false;

            },
            selectContextMenu(e) {
                var btnId = e.item.id;
                var index = e.target.parentElement.rowIndex;
                var row = this.$refs.sentEmailsGrid.kendoWidget().dataSource.data()[index]

                if (btnId === 'details') {
                    this.detailsBtnClick(row);
                }
            }
        },
        computed: {
            ...mapGetters(['gridOptions', 'authToken', 'dateTimeFormat']),
            customDateTimeFormat() {
                return `{0: ${this.dateTimeFormat} }`;
            },
        }
    };
</script>

<style lang="scss">
    tr.left-border-danger {
        &:hover

    {
        color: rgba(255, 0, 0, 0.7) !important;
    }

    td:first-child {
        border-left: 3px solid rgba(255, 0, 0, 0.5) !important;
    }

    }

    tr.left-border-success {
        &:hover

    {
        color: #4caf50 !important;
    }

    td:first-child {
        border-left: 3px solid #4caf50 !important;
    }

    }
</style>
